/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import ArgonButton from "components/ArgonButton";
import { LinearProgress, Tooltip } from "@mui/material";
import PropTypes from "prop-types";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

// Images
import team2 from "assets/images/team-2.jpg";
import { Link } from "react-router-dom";

function Author({ image, name, email }) {
  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image || team2} alt={name} size="sm" variant="full" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium">
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {email}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

const StudentsList = ({ students = [] }) => {
  if (students.length === 0) {
    return (
      <ArgonTypography variant="caption" color="error">
        No Students
      </ArgonTypography>
    );
  }

  const displayStudent = students[0]; // first student
  const extraCount = students.length - 1; // extra students count

  const content = (
    <ArgonBox display="flex" alignItems="center" gap={1}>
      <ArgonBox display="flex" flexDirection="column" alignItems="flex-start">
        <ArgonTypography
          textTransform="none"
          sx={{
            backgroundColor: "#f0f0f0",
            padding: "4px 7px",
            borderRadius: "6px",
          }}
          variant="caption"
          fontWeight="medium"
          color="dark"
        >
          {displayStudent} {extraCount > 0 && `+${extraCount}`}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );

  const tooltipContent = (
    <ArgonBox sx={{ maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
      <ArgonTypography
        variant="caption"
        fontWeight="bold"
        color="white"
        sx={{ display: "block", marginBottom: "8px" }}
      >
        All Students ({students.length}):
      </ArgonTypography>
      <ArgonBox>
        {students.map((student, index) => (
          <ArgonTypography
            key={index}
            variant="caption"
            color="white"
            sx={{ display: "block", marginBottom: "4px" }}
          >
            • {student}
          </ArgonTypography>
        ))}
      </ArgonBox>
    </ArgonBox>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            backdropFilter: "blur(8px)",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            animation: "fadeInUp 0.3s ease-out",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(10px) scale(0.95)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
            },
          },
        },
        arrow: {
          sx: {
            color: "rgba(0, 0, 0, 0.9)",
          },
        },
      }}
    >
      <ArgonBox sx={{ cursor: "pointer", display: "inline-block" }}>{content}</ArgonBox>
    </Tooltip>
  );
};

StudentsList.propTypes = {
  students: PropTypes.arrayOf(PropTypes.string),
};

// Build table data from API response
export const buildTutorPayrollTableData = (data = [], handleDeleteClick) => {
  const columns = [
    { name: "sno", align: "center" },
    { name: "tutors", align: "left" },
    { name: "student", align: "left" },
    { name: "Payroll Status", align: "left" },
    { name: "class name", align: "left" },
    { name: "status", align: "left" },
    // { name: "mode", align: "left" },
    { name: "last payment", align: "left" },
    { name: "action", align: "center" },
  ];

  // Helper function to get badge color based on payroll status
  const getPayrollStatusColor = (status) => {
    const statusMap = {
      PROCESSED: "success",
      PENDING: "warning",
      FAILED: "error",
      HOLD: "secondary",
      COMPLETED: "info",
    };
    return statusMap[status] || "secondary";
  };

  // Helper function to get status badge color
  const getStatusColor = (status) => {
    const statusMap = {
      ACTIVE: "success",
      INACTIVE: "secondary",
      COMPLETED: "info",
      HOLD: "warning",
    };
    return statusMap[status] || "secondary";
  };

  function RemainingProgress({ percentage }) {
    const normalizedPercentage = Math.min(100, Math.max(0, Number(percentage) || 0));
    const hasProgress = normalizedPercentage > 0;

    return (
      <ArgonBox position="relative" width="120px" height="25px">
        <ArgonBox
          width="100%"
          height="100%"
          bgColor="#E9ECEF"
          borderRadius="4px"
          position="relative"
          overflow="hidden"
          sx={{
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)",
            border: "1px solid #dee2e6",
          }}
        >
          {hasProgress && (
            <ArgonBox
              width={`${normalizedPercentage}%`}
              height="100%"
              bgColor="primary"
              borderRadius="4px"
              position="absolute"
              top={0}
              left={0}
              sx={{
                background: "linear-gradient(135deg, #6477e2ff 0%, #4c63d2 100%)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2), 0 1px 2px rgba(0,0,0,0.2)",
                border: "1px solid #3b4cb8",
              }}
            />
          )}
          <ArgonBox
            position="absolute"
            top="50%"
            left="50%"
            sx={{ transform: "translate(-50%, -50%)" }}
          >
            <ArgonTypography
              variant="caption"
              fontWeight="bold"
              color={hasProgress ? "white" : "text"}
              sx={{
                textShadow: hasProgress ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
                fontSize: "11px",
              }}
            >
              {normalizedPercentage}%
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>
    );
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Helper function to get class name from financeId
  const getClassName = (financeId) => {
    if (!financeId || !financeId.students || financeId.students.length === 0) {
      return "N/A";
    }
    const student = financeId.students[0];
    const className = student.class?.name || student.class || "";
    const boardName = student.board?.name || student.board || "";
    return `${className} - ${boardName}`.trim() || "N/A";
  };

  const rows = data.map((item, index) => ({
    sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {index + 1}
      </ArgonTypography>
    ),
    tutors: (
      <Author
        image={item.tutorId?.profileImage || item.tutorId?.image}
        name={item.tutorId?.fullName || "N/A"}
        email={item.tutorId?.email || "N/A"}
      />
    ),
    student: (
      <ArgonBox display="flex" flexDirection="column" gap={0.5}>
        <ArgonBox>
          <StudentsList
            students={
              item.allStudents && item.allStudents.length > 0
                ? item.allStudents.map((student) => student.name || "N/A")
                : item.financeId?.students && item.financeId.students.length > 0
                ? item.financeId.students.map((student) => student.name || "N/A")
                : []
            }
          />
        </ArgonBox>
        <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
          {item.financeId?.parentId?.fullName
            ? `Client- ${item.financeId.parentId.fullName}`
            : "N/A"}
        </ArgonTypography>
      </ArgonBox>
    ),
    "Payroll Status": (() => {
      const totalClasses = item.totalClasses || 0;
      const completedClasses = item.completedClasses || 0;
      const percentage = totalClasses > 0 ? Math.round((completedClasses / totalClasses) * 100) : 0;

      return <RemainingProgress percentage={percentage} />;
    })(),
    "class name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {getClassName(item.financeId)}
      </ArgonTypography>
    ),
    status: (
      <ArgonBadge
        variant="gradient"
        badgeContent={item.tutorId?.status || "ACTIVE"}
        color={getStatusColor(item.tutorId?.status)}
        size="xs"
        container
      />
    ),
    mode: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {item.cycleType || "N/A"}
      </ArgonTypography>
    ),
    "last payment": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {formatDate(item.lastPaymentDate || item.paymentDate || item.paidAt)}
      </ArgonTypography>
    ),
    action: (
      <ArgonBox display="flex" alignItems="center" gap={0}>
        {/* View Icon */}
        <ArgonButton
          variant="text"
          size="large"
          component={Link}
          to={`/tutorpayroll/view/${item.tutorId?._id || item._id}`}
          sx={{ minWidth: "12px", p: 0 }}
        >
          <VisibilityIcon color="primary" />
        </ArgonButton>
        {/* Delete icon */}
        {/* <ArgonButton
          variant="text"
          size="large"
          sx={{ minWidth: "32px", p: 0 }}
          onClick={() => handleDeleteClick(item)}
        >
          <DeleteIcon color="error" />
        </ArgonButton> */}
      </ArgonBox>
    ),
  }));

  return { columns, rows };
};
